import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Heart, 
  MessageCircle, 
  UserPlus, 
  Search, 
  Target,
  Users2,
  Home,
  Briefcase,
  UserCheck,
  BarChart3,
  Lightbulb,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { useUserStore, Contact } from '../stores/userStore';
import { useAuth } from '../contexts/AuthContext';
import GroupChatInterface from '../components/GroupChatInterface';
import ContactChartStatus from '../components/ContactChartStatus';
import toast from 'react-hot-toast';
import { astrologyChartAPI } from '../services/api';
import StarAnimation from '../components/StarAnimation';

interface GroupChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  taggedContacts?: Contact[];
  query?: string;
  isLoading?: boolean;
}

interface CompatibilityInsight {
  id: string;
  type: 'relationship' | 'team' | 'family' | 'business';
  title: string;
  description: string;
  score: number;
  contacts: Contact[];
  insights: string[];
  recommendations: string[];
}

const GroupCompatibility: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { user: storeUser, addContact, importPendingContactCharts, retryContactChartImport, syncWithAuth, updateUser } = useUserStore();
  
  // Use storeUser if available, otherwise use authUser
  const user = storeUser || authUser;
  
  // Sync userStore with AuthContext if userStore is empty
  useEffect(() => {
    if (authUser && !storeUser) {
      console.log('Syncing userStore with AuthContext:', authUser);
      syncWithAuth(authUser);
    }
  }, [authUser, storeUser, syncWithAuth]);

  // Fetch user's birth data from backend if not available (fallback)
  useEffect(() => {
    const fetchUserBirthData = async () => {
      if (user?.uid && !user.birthData) {
        try {
          const response = await fetch(`/api/hybrid-rag/user-profile/${user.uid}`);
          const result = await response.json();
          
          if (result.success && result.profile?.birthData) {
            const birthData = result.profile.birthData;
            
            // Update user store with birth data
            if (storeUser) {
              updateUser({ birthData });
            }
          }
        } catch (error) {
          console.error('Error fetching user birth data:', error);
        }
      }
    };

    fetchUserBirthData();
  }, [user?.uid, user?.birthData, storeUser, updateUser]);
  const contacts = storeUser?.contacts || [];
  
  const [isGroupChatOpen, setIsGroupChatOpen] = useState(false);
  const [groupMessages, setGroupMessages] = useState<GroupChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<CompatibilityInsight | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'family' | 'friends' | 'business'>('all');
  const [newContact, setNewContact] = useState({
    name: '',
    relationship: '',
    category: 'friends' as 'family' | 'friends' | 'business',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    minute: 0,
    latitude: 0,
    longitude: 0,
    timezone: 0,
    place_of_birth: ''
  });

  // Auto-import pending contact charts when component loads
  useEffect(() => {
    console.log('=== DEBUG INFO ===');
    console.log('AuthContext user:', authUser);
    console.log('UserStore user:', storeUser);
    console.log('Combined user:', user);
    console.log('User UID:', user?.uid);
    console.log('Contacts:', contacts);
    console.log('==================');
    
    if (user?.uid && contacts.length > 0) {
      const pendingContacts = contacts.filter(c => c.chartStatus === 'pending');
      console.log('Pending contacts:', pendingContacts);
      
      if (pendingContacts.length > 0) {
        console.log(`Found ${pendingContacts.length} pending contacts, starting auto-import...`);
        console.log('User ID for import:', user.uid);
        importPendingContactCharts().catch(error => {
          console.error('Error auto-importing pending contacts:', error);
        });
      }
    } else {
      console.log('Cannot import: user.uid =', user?.uid, 'contacts.length =', contacts.length);
    }
  }, [user?.uid, contacts.length]);

  // Mock compatibility insights for demonstration
  const [compatibilityInsights] = useState<CompatibilityInsight[]>([
    {
      id: '1',
      type: 'family',
      title: 'Family Harmony Analysis',
      description: 'Deep dive into family dynamics and relationships',
      score: 85,
      contacts: contacts.filter(c => c.category === 'family' || c.relationship.includes('family')),
      insights: [
        'Strong emotional bonds between family members',
        'Good communication patterns established',
        'Shared values and traditions strengthen bonds'
      ],
      recommendations: [
        'Schedule regular family gatherings',
        'Practice active listening during conversations',
        'Celebrate individual achievements together'
      ]
    },
    {
      id: '2',
      type: 'business',
      title: 'Professional Team Dynamics',
      description: 'Analyze workplace relationships and collaboration',
      score: 78,
      contacts: contacts.filter(c => c.category === 'business' || c.relationship.includes('work')),
      insights: [
        'Complementary skills enhance team performance',
        'Clear communication channels established',
        'Shared professional goals drive success'
      ],
      recommendations: [
        'Regular team building activities',
        'Open feedback sessions',
        'Clear role definitions and responsibilities'
      ]
    },
    {
      id: '3',
      type: 'relationship',
      title: 'Friendship Compatibility',
      description: 'Explore friendship dynamics and social connections',
      score: 92,
      contacts: contacts.filter(c => c.category === 'friends' || c.relationship.includes('friend')),
      insights: [
        'Strong mutual understanding and trust',
        'Shared interests and hobbies',
        'Supportive and encouraging relationships'
      ],
      recommendations: [
        'Plan regular meetups and activities',
        'Maintain open and honest communication',
        'Support each other\'s personal growth'
      ]
    }
  ]);

  const handleGroupChatMessage = async (message: string, taggedContacts: Contact[]) => {
    if (!user || !user.birthData) {
      const errorMessage: GroupChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Please provide your birth details in your profile first to get personalized astrological insights. You can add your birth date, time, and place in the Profile section.',
        timestamp: new Date()
      };
      setGroupMessages(prev => [...prev, errorMessage]);
      return;
    }

    setIsLoading(true);

    try {
      const conversationHistory = groupMessages.map(msg => ({
        type: msg.type,
        content: msg.content
      }));

      const birthData = user.birthData ? {
        day: user.birthData.day,
        month: user.birthData.month,
        year: user.birthData.year,
        hour: user.birthData.hour,
        minute: user.birthData.minute,
        latitude: user.birthData.latitude,
        longitude: user.birthData.longitude,
        place_of_birth: user.birthData.place_of_birth
      } : null;

      let enhancedMessage = message;
      if (taggedContacts.length > 0) {
        enhancedMessage += `\n\nTagged contacts for group analysis:\n${taggedContacts.map(contact => 
          `- ${contact.name} (${contact.relationship})${contact.birthData ? ` - Birth: ${contact.birthData.day}/${contact.birthData.month}/${contact.birthData.year}` : ''}`
        ).join('\n')}`;
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: enhancedMessage,
          userContext: {
            userId: user.uid || 'anonymous_user',
            birthData: birthData,
            userProfile: {
              name: user.name,
              birthData: birthData
            }
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        const assistantMessage: GroupChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.response,
          timestamp: new Date(),
          taggedContacts: taggedContacts
        };
        setGroupMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage: GroupChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.error || 'I apologize, but I couldn\'t process your request. Please try again.',
          timestamp: new Date()
        };
        setGroupMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error processing group chat message:', error);
      
      const errorMessage: GroupChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setGroupMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const addNewContact = () => {
    if (!newContact.name || !newContact.relationship) {
      toast.error('Please fill in name and relationship');
      return;
    }

    addContact({
      name: newContact.name,
      relationship: newContact.relationship,
      category: newContact.category,
      birthData: {
        day: newContact.day,
        month: newContact.month,
        year: newContact.year,
        hour: newContact.hour,
        minute: newContact.minute,
        latitude: newContact.latitude,
        longitude: newContact.longitude,
        timezone: newContact.timezone,
        place_of_birth: newContact.place_of_birth
      }
    });

    setNewContact({
      name: '',
      relationship: '',
      category: 'friends',
      day: 1,
      month: 1,
      year: 1990,
      hour: 12,
      minute: 0,
      latitude: 0,
      longitude: 0,
      timezone: 0,
      place_of_birth: ''
    });

    setShowContactForm(false);
    toast.success('Contact added successfully!');
  };

  const openGroupChat = () => {
    navigate('/group-compatibility-chat');
  };

  const getFilteredContacts = () => {
    let filtered = contacts;
    if (filterType !== 'all') {
      filtered = contacts.filter(contact => contact.category === filterType || 
        (filterType === 'family' && contact.relationship.includes('family')) ||
        (filterType === 'friends' && contact.relationship.includes('friend')) ||
        (filterType === 'business' && contact.relationship.includes('business')));
    }
    if (searchQuery) {
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.relationship.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return filtered;
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'family': return <Home className="w-4 h-4" />;
      case 'friends': return <Heart className="w-4 h-4" />;
      case 'business': return <Briefcase className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'family': return 'text-blue-500 bg-blue-100';
      case 'friends': return 'text-pink-500 bg-pink-100';
      case 'business': return 'text-green-500 bg-green-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const handleContactClick = (contact: Contact) => {
    // This function is not fully implemented in the original file,
    // but the new_code_to_apply_changes_from includes it.
    // For now, we'll just log the click.
    console.log('Contact clicked:', contact);
    // In a real app, you might open a modal or navigate to a detail page
    // for the specific contact's compatibility insights.
  };

  // Test function to verify backend connection
  const testBackendConnection = async () => {
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: "Test message from frontend",
          userContext: {
            userId: user?.uid || 'test_user'
          }
        })
      });

      const result = await response.json();
      console.log('Backend test response:', result);
      
      if (result.success) {
        toast.success('✅ Backend connection working!');
      } else {
        toast.error('❌ Backend connection failed');
      }
    } catch (error) {
      console.error('Backend connection test failed:', error);
      toast.error('❌ Backend connection failed');
    }
  };

  // Delete contact function
  const deleteContact = async (contactName: string) => {
    try {
      const response = await fetch(`/api/hybrid-rag/user-contacts/${user?.uid}/${contactName}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`✅ Contact '${contactName}' deleted successfully`);
        // Refresh contacts by syncing with auth
        if (user) {
          syncWithAuth(user);
        }
      } else {
        toast.error(`❌ Failed to delete contact: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('❌ Failed to delete contact');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Star Animation */}
      <StarAnimation starCount={20} />
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-4">
            <Users2 className="w-8 h-8 text-purple-400 mr-3" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Group Compatibility</h1>
          </div>
          <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Deep dive into relationships with friends, family, and business colleagues
          </p>
          {(!user || !user.birthData) && (
            <div className="mt-4 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg max-w-2xl mx-auto">
              <p className="text-yellow-300 text-sm text-center">
                💡 <strong>Tip:</strong> Add your birth details in the Profile section to get personalized compatibility insights!
              </p>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <button
            onClick={openGroupChat}
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
          >
            <div className="flex items-center justify-center mb-3">
              <MessageCircle className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-white font-semibold text-center">Open Chat</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Full-screen compatibility chat</p>
          </button>

          <button
            onClick={() => setShowContactForm(true)}
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
          >
            <div className="flex items-center justify-center mb-3">
              <UserPlus className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-white font-semibold text-center">Add Contact</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Add friends & family</p>
          </button>

          <button
            onClick={testBackendConnection}
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
          >
            <div className="flex items-center justify-center mb-3">
              <RefreshCw className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-white font-semibold text-center">Test Connection</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Verify backend</p>
          </button>

          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex items-center justify-center mb-3">
              <BarChart3 className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-white font-semibold text-center">Insights</h3>
            <p className="text-gray-300 text-sm text-center mt-2">{compatibilityInsights.length} reports</p>
          </div>

          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex items-center justify-center mb-3">
              <Users className="w-8 h-8 text-pink-400" />
            </div>
            <h3 className="text-white font-semibold text-center">Contacts</h3>
            <p className="text-gray-300 text-sm text-center mt-2">{contacts.length} people</p>
          </div>
        </motion.div>

        {/* Compatibility Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Compatibility Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {compatibilityInsights.map((insight, index) => (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedInsight(insight)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    {insight.type === 'family' && <Home className="w-5 h-5 text-blue-400 mr-2" />}
                    {insight.type === 'team' && <Users className="w-5 h-5 text-green-400 mr-2" />}
                    {insight.type === 'business' && <Briefcase className="w-5 h-5 text-purple-400 mr-2" />}
                    <h3 className="text-white font-semibold">{insight.title}</h3>
                  </div>
                  <div className={`text-lg font-bold ${getScoreColor(insight.score)}`}>
                    {insight.score}%
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-4">{insight.description}</p>
                <div className="flex items-center text-sm text-gray-400">
                  <Users className="w-4 h-4 mr-1" />
                  {insight.contacts.length} contacts
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contacts Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-4 sm:mb-0">Your Contacts</h2>
            <div className="flex gap-2">
              {/* Manual sync button for testing */}
              <button
                onClick={() => {
                  console.log('Manual sync clicked');
                  console.log('AuthContext user:', authUser);
                  if (authUser) {
                    syncWithAuth(authUser);
                    toast.success('User data synced!');
                  } else {
                    toast.error('No auth user available');
                  }
                }}
                className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-300 hover:bg-blue-500/30 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Sync User
              </button>
              
              {/* Manual import button for pending contacts */}
              {contacts.filter(c => c.chartStatus === 'pending' || c.chartStatus === 'failed').length > 0 && (
                <button
                  onClick={() => {
                    importPendingContactCharts();
                    toast.success('Starting chart import for pending contacts...');
                  }}
                  className="px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-300 hover:bg-yellow-500/30 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Import Pending Charts
                </button>
              )}
              
              <button
                onClick={() => setShowContactForm(true)}
                className="px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 hover:bg-green-500/30 transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Add Contact
              </button>
              
              {/* Add sample contacts for testing */}
              <button
                onClick={() => {
                  const sampleContacts = [
                    {
                      name: 'John Doe',
                      relationship: 'Friend',
                      category: 'friends' as const,
                      day: 15,
                      month: 6,
                      year: 1990,
                      hour: 14,
                      minute: 30,
                      latitude: 40.7128,
                      longitude: -74.0060,
                      timezone: -5,
                      place_of_birth: 'New York, NY'
                    },
                    {
                      name: 'Jane Smith',
                      relationship: 'Sister',
                      category: 'family' as const,
                      day: 20,
                      month: 8,
                      year: 1988,
                      hour: 10,
                      minute: 15,
                      latitude: 34.0522,
                      longitude: -118.2437,
                      timezone: -8,
                      place_of_birth: 'Los Angeles, CA'
                    }
                  ];
                  
                  sampleContacts.forEach(contact => {
                    addContact(contact);
                  });
                  
                  toast.success('Added sample contacts!');
                }}
                className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-300 hover:bg-purple-500/30 transition-colors flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Add Sample Contacts
              </button>
              
              {/* Test contact import with known user ID */}
              <button
                onClick={async () => {
                  try {
                    const testContact = {
                      name: 'Test Contact',
                      day: 15,
                      month: 6,
                      year: 1990,
                      hour: 14,
                      minute: 30,
                      latitude: 40.7128,
                      longitude: -74.0060,
                      timezone: -5,
                      place_of_birth: 'New York, NY'
                    };
                    
                    console.log('Testing contact import with user ID:', user?.uid);
                    console.log('Test contact data:', testContact);
                    
                    const result = await astrologyChartAPI.importContactCharts(
                      'JhsRHjOheDfz0lugMwXCB5vZm5o1', // Known user ID from backend
                      'contact_test_manual',
                      'Test Contact',
                      testContact
                    );
                    
                    console.log('Test import result:', result);
                    toast.success('Test import completed!');
                  } catch (error) {
                    console.error('Test import failed:', error);
                    toast.error('Test import failed!');
                  }
                }}
                className="px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-orange-300 hover:bg-orange-500/30 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Test Import
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {getFilteredContacts().map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">{contact.name}</h3>
                    <p className="text-sm text-gray-300">{contact.relationship}</p>
                    {contact.category && (
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                        contact.category === 'family' ? 'bg-blue-100 text-blue-800' :
                        contact.category === 'friends' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {contact.category}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <ContactChartStatus 
                      contact={contact} 
                      onRetry={retryContactChartImport}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Are you sure you want to delete ${contact.name}?`)) {
                          deleteContact(contact.name);
                        }
                      }}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                      title="Delete contact"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleContactClick(contact)}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {contact.birthData && (
                  <div className="text-xs text-gray-400">
                    Birth: {contact.birthData.day}/{contact.birthData.month}/{contact.birthData.year}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {getFilteredContacts().length === 0 && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No contacts found. Add some contacts to get started!</p>
            </div>
          )}
        </motion.div>

        {/* Floating Chat Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
          onClick={openGroupChat}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 z-50"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </motion.button>

        {/* Contact Form Modal */}
        <AnimatePresence>
          {showContactForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowContactForm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-800 rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-white mb-4">Add New Contact</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Name</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                      placeholder="Enter name"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Relationship</label>
                    <input
                      type="text"
                      value={newContact.relationship}
                      onChange={(e) => setNewContact({...newContact, relationship: e.target.value})}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                      placeholder="e.g., Best friend, Sister, Colleague"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Category</label>
                    <select
                      value={newContact.category}
                      onChange={(e) => setNewContact({...newContact, category: e.target.value as any})}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    >
                      <option value="friends">Friends</option>
                      <option value="family">Family</option>
                      <option value="business">Business</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Day</label>
                      <input
                        type="number"
                        value={newContact.day}
                        onChange={(e) => setNewContact({...newContact, day: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        min="1"
                        max="31"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Month</label>
                      <input
                        type="number"
                        value={newContact.month}
                        onChange={(e) => setNewContact({...newContact, month: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        min="1"
                        max="12"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Year</label>
                      <input
                        type="number"
                        value={newContact.year}
                        onChange={(e) => setNewContact({...newContact, year: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                        min="1900"
                        max="2024"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={addNewContact}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                    >
                      Add Contact
                    </button>
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="flex-1 bg-white/10 text-white py-2 px-4 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Insight Detail Modal */}
        <AnimatePresence>
          {selectedInsight && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedInsight(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-white">{selectedInsight.title}</h3>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedInsight.score)}`}>
                    {selectedInsight.score}%
                  </div>
                </div>

                <p className="text-gray-300 mb-6">{selectedInsight.description}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Lightbulb className="w-5 h-5 text-yellow-400 mr-2" />
                      Key Insights
                    </h4>
                    <ul className="space-y-2">
                      {selectedInsight.insights.map((insight, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Target className="w-5 h-5 text-green-400 mr-2" />
                      Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {selectedInsight.recommendations.map((rec, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <Users className="w-5 h-5 text-blue-400 mr-2" />
                    Contacts ({selectedInsight.contacts.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedInsight.contacts.map((contact) => (
                      <div key={contact.id} className="flex items-center p-3 bg-white/5 rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getCategoryColor(contact.category)}`}>
                          {getCategoryIcon(contact.category)}
                        </div>
                        <div>
                          <div className="text-white font-medium">{contact.name}</div>
                          <div className="text-gray-400 text-sm">{contact.relationship}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => {
                      setSelectedInsight(null);
                      openGroupChat();
                    }}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center justify-center"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat About This
                  </button>
                  <button
                    onClick={() => setSelectedInsight(null)}
                    className="flex-1 bg-white/10 text-white py-2 px-4 rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Group Chat Interface */}
      <GroupChatInterface
        isOpen={isGroupChatOpen}
        messages={groupMessages}
        onSendMessage={(message, taggedContacts) => handleGroupChatMessage(message, taggedContacts)}
        onClose={() => setIsGroupChatOpen(false)}
        isLoading={isLoading}
        title="Group Compatibility Analysis"
        subtitle="Analyze relationships with friends, family, and colleagues"
        placeholder="Ask about group dynamics, team synergy, family harmony..."
      />
    </div>
  );
};

export default GroupCompatibility; 